import { useState } from 'react'
import { SparklesIcon, MailIcon, PhoneIcon } from './icons.jsx'
import { asList, isKnown, splitCitation, splitVerdict } from '../lib/enrichment.js'

// What enrichment bought: the play to run, the opportunity, who owns the
// budget, then the profile. The research is written around whatever this
// workspace sells (SELLER_PROFILE on the enrichment worker), so nothing here is
// industry-specific. Kept deliberately flat — one line per fact, sections
// skipped entirely when the research couldn't establish them.

function SourceTag({ source }) {
  if (!source) return null
  return (
    <span className="ml-1.5 whitespace-nowrap text-[11.5px] text-faint" title={`Source: ${source}`}>
      {source}
    </span>
  )
}

function Cited({ value, className = '' }) {
  const { text, source } = splitCitation(value)
  return (
    <span className={className}>
      {text}
      <SourceTag source={source} />
    </span>
  )
}

// One researched fact: quiet label left, claim right — a single line.
function Fact({ label, value }) {
  if (!isKnown(value)) return null
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]">
      <span className="w-[130px] flex-none text-[12px] text-faint">{label}</span>
      <Cited value={value} className="min-w-[220px] flex-1 text-[13.5px] leading-[1.5] text-ink" />
    </div>
  )
}

function Bullets({ items, marker = 'bg-line' }) {
  const list = asList(items)
  if (!list.length) return null
  return (
    <ul className="m-0 flex list-none flex-col gap-y-1.5 p-0">
      {list.map((t, i) => (
        <li key={`${t}-${i}`} className="flex items-start gap-x-2">
          <span className={`mt-[7px] h-[5px] w-[5px] flex-none rounded-full ${marker}`} />
          <Cited value={t} className="text-[13.5px] leading-[1.55] text-ink" />
        </li>
      ))}
    </ul>
  )
}

// "yes — Jane Doe (Owner, per x.com)" → verdict pill + detail.
function PersonRow({ label, value }) {
  if (!isKnown(value)) return null
  const { verdict, detail } = splitVerdict(value)
  if (!detail && !verdict) return null
  const tone =
    verdict === 'yes'
      ? 'bg-[#f0fdf4] text-[#15803d] dark:bg-[rgba(34,197,94,0.15)] dark:text-[#4ade80]'
      : verdict === 'no'
        ? 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(185,28,28,0.2)] dark:text-[#fca5a5]'
        : 'bg-panel text-dim'
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-2 [&:not(:last-child)]:[border-bottom:1px_solid_var(--table-line)]">
      <span className="w-[130px] flex-none text-[12px] text-faint">{label}</span>
      {verdict && (
        <span className={`flex-none rounded-[6px] px-[7px] py-px text-[10.5px] font-bold uppercase tracking-[0.03em] ${tone}`}>
          {verdict}
        </span>
      )}
      <Cited value={detail} className="min-w-[200px] flex-1 text-[13.5px] leading-[1.5] text-ink" />
    </div>
  )
}

function CopyButton({ text }) {
  const [done, setDone] = useState(false)
  if (!text) return null
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(
          () => {
            setDone(true)
            setTimeout(() => setDone(false), 1600)
          },
          () => {},
        )
      }}
      className="flex-none rounded-[7px] border border-line bg-surface px-2 py-1 text-[11.5px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
    >
      {done ? 'Copied' : 'Copy'}
    </button>
  )
}

function Section({ title, children }) {
  return (
    <section className="pt-4 [&:not(:first-of-type)]:mt-4 [&:not(:first-of-type)]:[border-top:1px_solid_var(--table-line)]">
      <h3 className="m-0 text-[13px] font-bold tracking-[-0.01em] text-ink">{title}</h3>
      <div className="mt-2">{children}</div>
    </section>
  )
}

function scoreTone(score) {
  if (score >= 8) return 'bg-[#f0fdf4] text-[#15803d] dark:bg-[rgba(34,197,94,0.15)] dark:text-[#4ade80]'
  if (score >= 5) return 'bg-[#fefce8] text-[#a16207] dark:bg-[rgba(161,98,7,0.2)] dark:text-[#fbbf24]'
  return 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(185,28,28,0.2)] dark:text-[#fca5a5]'
}

// Shown before enrichment — names what the click buys.
// `researched` means the lead already carries research — it arrived enriched in
// the import (score, sales angles, objections, owners) — but no AI dossier has
// been bought for it yet. Saying "Not enriched yet" to that lead is simply
// false, so the teaser says what exists and offers the deeper run on top.
export function ResearchTeaser({ onRun, disabled, running, note, researched = false }) {
  return (
    <div className="rounded-[14px] border border-dashed border-line bg-panel p-5">
      <div className="flex flex-wrap items-center gap-x-2">
        <SparklesIcon className="h-[17px] w-[17px] text-primary" />
        <h2 className="m-0 text-[15px] font-bold tracking-[-0.01em] text-ink">
          {running ? 'Enriching…' : researched ? 'Researched in the import' : 'Not enriched yet'}
        </h2>
      </div>
      <p className="m-0 mt-1.5 max-w-[560px] text-[13.5px] leading-[1.55] text-dim">
        {researched
          ? 'This lead came researched — its score, sales angles, objections and named people are in Sales intel below. The AI run goes further and live: it reads their site, reviews, news and hiring posts, sizes the opportunity, and adds verified contacts.'
          : "Enrich runs the AI research: it reads their site, reviews, news and hiring posts, then sizes the opportunity — what they sell, what's hurting, how they solve it today, who owns the budget, and the opener to send. Verified contacts and a 0–10 fit score come with it."}
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {[
          'What they sell',
          'Recent activity',
          'How they solve it today',
          'Who owns the budget',
          'Buying signals',
          'Opener + talking points',
        ].map((t) => (
          <span key={t} className="rounded-[7px] bg-surface px-2 py-[3px] text-[12px] text-dim">
            {t}
          </span>
        ))}
      </div>
      {/* A run is already in flight (the header says so too) — offering
          "Enrich this lead" here contradicts that and invites a second paid
          batch, so the call to action is replaced by its progress. */}
      {running ? (
        <div className="mt-4 flex items-center gap-x-2">
          <span
            className="h-[15px] w-[15px] animate-spin rounded-full [border:2px_solid_var(--table-line)] [border-top-color:var(--primary)]"
            role="status"
            aria-label="Researching this lead"
          />
          <span className="text-[13px] font-semibold text-dim">
            Researching this lead — findings land here when it finishes.
          </span>
        </div>
      ) : (
        onRun && (
          <button
            type="button"
            onClick={onRun}
            disabled={disabled}
            title={note || undefined}
            className="mt-4 inline-flex items-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[8px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            <SparklesIcon className="h-4 w-4" />
            {researched ? 'Run AI research' : 'Enrich this lead'}
          </button>
        )
      )}
      {note && !running && <p className="m-0 mt-2 text-[12px] text-faint">{note}</p>}
    </div>
  )
}

export default function LeadResearch({ dossier, loading, onRefresh, refreshNote = '' }) {
  const [showSources, setShowSources] = useState(false)

  if (loading) {
    return (
      <div className="flex min-h-[160px] flex-col items-center justify-center gap-y-3 rounded-[14px] border border-line bg-surface">
        <span
          className="h-6 w-6 animate-spin rounded-full [border:2px_solid_var(--table-line)] [border-top-color:var(--primary)]"
          role="status"
          aria-label="Loading research"
        />
        <span className="text-[13px] text-dim">Loading research…</span>
      </div>
    )
  }

  const r = dossier?.result || {}
  const score = dossier?.rankedScore ?? (Number.isFinite(r.ranked_score) ? r.ranked_score : null)
  const why = r.ranked_reasoning || dossier?.reasoning || r.priority_reason || ''
  const sources = asList(r.sources)
    .map((u) => String(u).replace(/[?&]utm_source=[^&\s]+/g, ''))
    .filter((u) => /^https?:\/\//i.test(u))

  const hasPlan =
    isKnown(r.opener) || asList(r.talking_points).length || isKnown(r.recommended_offer) || isKnown(r.likely_objection)
  const hasOpportunity =
    isKnown(r.recent_activity) || asList(r.pain_points).length || isKnown(r.current_solution) || isKnown(r.offering_mix)
  const hasPeople = isKnown(r.owners) || isKnown(r.decision_makers)
  const hasProfile =
    isKnown(r.years_in_business) ||
    isKnown(r.est_company_size) ||
    isKnown(r.est_annual_volume) ||
    isKnown(r.market_footprint) ||
    isKnown(r.review_profile)
  const signals = asList(r.buying_signals)
  const risks = asList(r.risk_flags)
  const pros = asList(r.pros)
  const cons = asList(r.cons)

  const anything = hasPlan || hasOpportunity || hasPeople || hasProfile || signals.length || pros.length
  if (!anything) {
    return (
      <div className="rounded-[14px] border border-line bg-surface p-5">
        <div className="flex flex-wrap items-center gap-x-2">
          <SparklesIcon className="h-[17px] w-[17px] text-primary" />
          <h2 className="m-0 text-[15px] font-bold tracking-[-0.01em] text-ink">AI research</h2>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="ml-auto flex-none rounded-[7px] border border-line bg-surface px-2.5 py-1 text-[12px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
            >
              Re-run
            </button>
          )}
        </div>
        <p className="m-0 mt-2 text-[13.5px] text-dim">
          The research ran but found nothing verifiable about this company online. Small, offline
          operators land here — the phone is the better route.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-[14px] border border-line bg-surface p-5">
      {/* Header: the one number, the one sentence */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
        <SparklesIcon className="h-[17px] w-[17px] text-primary" />
        <h2 className="m-0 text-[15px] font-bold tracking-[-0.01em] text-ink">AI research</h2>
        {score != null && score >= 0 && (
          <span className={`rounded-[7px] px-2 py-[3px] text-[12px] font-bold ${scoreTone(score)}`}>
            Fit {score}/10
          </span>
        )}
        <span className="ml-auto flex flex-none items-center gap-x-2">
          {sources.length > 0 && (
            <button
              type="button"
              onClick={() => setShowSources((v) => !v)}
              className="border-none bg-transparent p-0 text-[12px] font-semibold text-primary-dark"
            >
              {showSources ? 'Hide sources' : `${sources.length} sources`}
            </button>
          )}
          {/* Disabled rather than hidden when it's too soon: a missing button
              reads as a bug, a disabled one with a reason reads as a rule. */}
          <button
            type="button"
            onClick={onRefresh || undefined}
            disabled={!onRefresh}
            title={onRefresh ? 'Re-run the AI research from scratch — costs another run' : refreshNote}
            className="rounded-[7px] border border-line bg-surface px-2.5 py-1 text-[12px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            Re-run
          </button>
        </span>
      </div>
      {why && <p className="m-0 mt-2 text-[13.5px] leading-[1.55] text-dim">{why}</p>}
      {showSources && sources.length > 0 && (
        <ul className="m-0 mt-3 flex list-none flex-col gap-y-1 rounded-[10px] bg-panel p-3">
          {sources.map((u) => (
            <li key={u} className="min-w-0">
              <a
                href={u}
                target="_blank"
                rel="noreferrer"
                className="block truncate text-[12.5px] text-primary-dark no-underline hover:underline"
              >
                {u}
              </a>
            </li>
          ))}
        </ul>
      )}

      {/* The play */}
      {hasPlan && (
        <Section title="The play">
          {isKnown(r.opener) && (
            <div className="flex items-start gap-x-2 rounded-[10px] bg-primary-tint p-3">
              <p className="m-0 flex-1 text-[13.5px] italic leading-[1.55] text-ink">
                “{splitCitation(r.opener).text}”
              </p>
              <CopyButton text={splitCitation(r.opener).text} />
            </div>
          )}
          {asList(r.talking_points).length > 0 && (
            <div className="mt-2.5">
              <Bullets items={r.talking_points} marker="bg-primary" />
            </div>
          )}
          <div className="mt-1.5">
            <Fact label="They'll say" value={r.likely_objection} />
            <Fact label="Offer" value={r.recommended_offer} />
            <Fact label="Channel" value={r.best_channel} />
          </div>
        </Section>
      )}

      {/* The opportunity — why this lead is money */}
      {(hasOpportunity || signals.length > 0 || risks.length > 0) && (
        <Section title="The opportunity">
          <Fact label="What they sell" value={r.offering_mix} />
          <Fact label="Recent activity" value={r.recent_activity} />
          {asList(r.pain_points).length > 0 && (
            <Fact label="Pain points" value={asList(r.pain_points).join(' · ')} />
          )}
          <Fact label="Solving it today" value={r.current_solution} />
          {signals.length > 0 && (
            <div className="pt-2.5">
              <div className="mb-1.5 text-[12px] font-semibold text-[#15803d] dark:text-[#4ade80]">
                Buying signals
              </div>
              <Bullets items={signals} marker="bg-[#4ade80]" />
            </div>
          )}
          {risks.length > 0 && (
            <div className="pt-2.5">
              <div className="mb-1.5 text-[12px] font-semibold text-[#b91c1c] dark:text-[#fca5a5]">
                Watch out
              </div>
              <Bullets items={risks} marker="bg-[#f87171]" />
            </div>
          )}
        </Section>
      )}

      {/* Who to call */}
      {hasPeople && (
        <Section title="Who to call">
          <PersonRow label="Owners" value={r.owners} />
          <PersonRow label="Decision makers" value={r.decision_makers} />
        </Section>
      )}

      {/* Company, compact */}
      {hasProfile && (
        <Section title="Company">
          <Fact label="In business" value={r.years_in_business} />
          <Fact label="Headcount" value={r.est_company_size} />
          <Fact label="Est. volume" value={r.est_annual_volume} />
          <Fact label="Markets" value={r.market_footprint} />
          <Fact label="Reviews" value={r.review_profile} />
        </Section>
      )}

      {/* The honest read */}
      {(pros.length > 0 || cons.length > 0) && (
        <Section title="Fit read">
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {pros.length > 0 && <Bullets items={pros} marker="bg-[#4ade80]" />}
            {cons.length > 0 && <Bullets items={cons} marker="bg-[#f87171]" />}
          </div>
        </Section>
      )}

      {dossier?.contactsNote && (
        <p className="m-0 mt-4 rounded-[10px] bg-panel px-3 py-2 text-[12px] text-dim">{dossier.contactsNote}</p>
      )}
    </div>
  )
}

// Contact rows gain titles/emails/LinkedIn after Apollo matching.
export function EnrichedContact({ contact }) {
  const email = contact.apollo_email || contact.email || ''
  const title = contact.apollo_title || contact.role || ''
  const linkedin = contact.apollo_linkedin || ''
  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-[14px] font-semibold text-ink">{contact.name}</span>
        {contact.apollo_location && <span className="text-[12px] text-faint">{contact.apollo_location}</span>}
      </div>
      {title && <div className="text-[12.5px] text-faint">{title}</div>}
      <div className="mt-[3px] flex flex-wrap items-center gap-x-3 gap-y-1">
        {contact.phone && (
          <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-x-1 text-[12.5px] text-primary-dark no-underline hover:underline">
            <PhoneIcon className="h-[13px] w-[13px]" />
            {contact.phone}
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} className="inline-flex items-center gap-x-1 text-[12.5px] text-primary-dark no-underline hover:underline">
            <MailIcon className="h-[13px] w-[13px]" />
            {email}
          </a>
        )}
        {linkedin && (
          <a href={linkedin} target="_blank" rel="noreferrer" className="text-[12.5px] text-primary-dark no-underline hover:underline">
            LinkedIn
          </a>
        )}
      </div>
    </div>
  )
}
